import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Seo, Eyebrow, CTAButton } from "@/components/site/primitives";
import { Reveal, Stagger, Item } from "@/components/site/motion";
import { PageHero, Section, Pill } from "@/components/site/pageparts";
import { TextField, TextArea, SubmitButton } from "@/components/site/forms";
import { HumanCheck } from "@/components/site/HumanCheck";
import { api } from "@/lib/api";
import { COMPANY } from "@/data/site";

const STEPS = [
  { n: "01", t: "Scope", d: "We sit with your team, learn the domain, and define the novel problem precisely — what memory has to do, and how you'll know it works." },
  { n: "02", t: "Embed", d: "A forward-deployed engineer works alongside you, inside your constraints — data residency, compliance, infrastructure and all." },
  { n: "03", t: "Build", d: "We build the complete solution on the DataFrontier substrate — ingestion, retrieval, governance and the surfaces your users touch." },
  { n: "04", t: "Handover", d: "You get a running system, the evaluation harness, and the knowledge to own it. No black box, no lock-in." },
];

const WHEN = [
  "The problem has no off-the-shelf answer and needs memory at its core.",
  "Data cannot leave your premises, and generic cloud tools are a non-starter.",
  "You need a working system, not a slide deck — measured against real numbers.",
  "Compliance (DPDP, GDPR, sector rules) is a first-class requirement, not an afterthought.",
];

export default function ForwardDeployed() {
  const [loading, setLoading] = useState(false);
  const [human, setHuman] = useState({ valid: false, honeypot: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!human.valid) {
      toast.error("Please answer the spam check to continue.");
      return;
    }
    const f = e.target;
    setLoading(true);
    try {
      await api.post("/contact", {
        name: f.name.value,
        email: f.email.value,
        organization: f.organization.value,
        intent: "Forward Deployed Engineering",
        message: f.message.value,
        company_website: human.honeypot,
      });
      toast.success("Thank you — a forward-deployed engineer will be in touch shortly.");
      f.reset();
    } catch (err) {
      const s = err?.response?.status;
      const msg =
        s === 429
          ? "Too many messages from this network. Please try again shortly."
          : s === 502
          ? "Our email provider is momentarily unavailable — please retry in a minute, or email hello@datafrontier.co."
          : "Something went wrong. Please email hello@datafrontier.co.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="fde-page">
      <Seo title="Forward Deployed Engineering" description="We embed a forward-deployed engineer to solve your novel problem and build a complete solution on the DataFrontier substrate." />
      <PageHero
        eyebrow="Company · Forward Deployed Engineering"
        title="We embed an engineer to solve your novel problem — end to end."
        lead="Some problems don't have an off-the-shelf answer. For those, we deploy a forward-deployed engineer who learns your domain, works inside your constraints, and builds the complete solution on our memory substrate."
      >
        <CTAButton href="#fde-form" variant="solid">
          Deploy an engineer <ArrowRight className="h-4 w-4" />
        </CTAButton>
        <Pill tone="saffron">Hands-on · production-grade</Pill>
      </PageHero>

      <Section>
        <Reveal>
          <Eyebrow>How it works</Eyebrow>
          <h2 className="mt-5 max-w-3xl font-serif text-3xl leading-[1.06] tracking-tighter text-bone md:text-5xl">
            An engineer in the room, not a ticket in a queue.
          </h2>
        </Reveal>
        <Stagger className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s) => (
            <Item key={s.n} className="bg-ink p-7">
              <div className="font-mono text-sm text-saffron">{s.n}</div>
              <h3 className="mt-5 font-serif text-xl text-bone">{s.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-mist">{s.d}</p>
            </Item>
          ))}
        </Stagger>
      </Section>

      <Section className="border-t border-white/10">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>When it fits</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl leading-tight tracking-tighter text-bone md:text-4xl">
              Built for the hard, the sensitive, and the novel.
            </h2>
          </Reveal>
          <Stagger className="lg:col-span-7">
            <div className="divide-y divide-white/10 border-y border-white/10">
              {WHEN.map((w, i) => (
                <Item key={i}>
                  <div className="flex items-start gap-4 py-5">
                    <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 bg-saffron" />
                    <p className="text-[15px] leading-relaxed text-mist">{w}</p>
                  </div>
                </Item>
              ))}
            </div>
          </Stagger>
        </div>
      </Section>

      {/* Contact form */}
      <Section id="fde-form" className="border-t border-white/10">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <Eyebrow>Start a project</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl tracking-tighter text-bone md:text-4xl">Tell us the problem.</h2>
            <p className="mt-6 text-[15px] leading-relaxed text-mist">
              Describe what you're trying to solve and the constraints you're under. We'll reply with whether a
              forward-deployed engagement is the right fit.
            </p>
            <p className="mt-6 text-sm text-mist">
              Or email{" "}
              <a href={`mailto:${COMPANY.emails.hello}`} className="link-underline pb-0.5 text-bone">{COMPANY.emails.hello}</a>.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <form onSubmit={submit} className="grid gap-6 sm:grid-cols-2" data-testid="fde-form-el">
              <TextField id="name" label="Your name" name="name" required placeholder="Ada Lovelace" />
              <TextField id="email" label="Work email" name="email" type="email" required placeholder="you@company.com" />
              <div className="sm:col-span-2">
                <TextField id="organization" label="Organization" name="organization" placeholder="Company / institution" />
              </div>
              <div className="sm:col-span-2">
                <TextArea id="message" label="The problem" name="message" required rows={6} placeholder="What are you trying to solve, and what are the constraints…" />
              </div>
              <div className="sm:col-span-2">
                <HumanCheck onChange={setHuman} />
              </div>
              <div className="sm:col-span-2">
                <SubmitButton loading={loading} disabled={!human.valid}>Deploy an engineer</SubmitButton>
              </div>
            </form>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
